import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Chip, Stack, Typography } from '@mui/material';

/**
 * Banner informativo + chips filtrables con contador.
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.description — 1–2 oraciones, tono neutral
 * @param {React.ElementType} props.icon — componente de icono MUI
 * @param {'primary'|'secondary'|'success'|'warning'|'error'|'info'} [props.accentColor='primary']
 * @param {React.ReactNode} [props.headerAction]
 * @param {Array<{key:string,label:string,count?:number,color?:string}>} [props.chips]
 * @param {string} [props.selectedChip]
 * @param {(key:string)=>void} [props.onChipChange]
 */
export default function ListadoOperativoBanner({
    title,
    description,
    icon: Icon,
    accentColor = 'primary',
    headerAction = null,
    chips = [],
    selectedChip = null,
    onChipChange = null,
}) {
    const theme = useTheme();
    const accent = theme.palette[accentColor] || theme.palette.primary;

    return (
        <Box
            sx={{
                mb: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${alpha(accent.main, 0.08)} 0%, ${alpha(accent.main, 0.02)} 55%, transparent 100%)`,
            }}
        >
            <Box
                sx={{
                    height: 5,
                    background: `linear-gradient(90deg, ${accent.dark || accent.main} 0%, ${alpha(accent.main, 0.55)} 55%, transparent 100%)`,
                }}
            />

            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{
                        alignItems: { xs: 'stretch', sm: 'flex-start' },
                        justifyContent: 'space-between',
                    }}
                >
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
                        {Icon ? (
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    bgcolor: alpha(accent.main, 0.18),
                                    color: accent.main,
                                }}
                            >
                                <Icon fontSize="medium" />
                            </Box>
                        ) : null}

                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                                {title}
                            </Typography>
                            {description ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.75, lineHeight: 1.55, maxWidth: 720 }}
                                >
                                    {description}
                                </Typography>
                            ) : null}
                        </Box>
                    </Stack>

                    {headerAction ? (
                        <Box sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
                            {React.isValidElement(headerAction)
                                ? React.cloneElement(headerAction, {
                                    sx: {
                                        width: { xs: '100%', sm: 'auto' },
                                        fontWeight: 700,
                                        ...(headerAction.props?.sx || {}),
                                    },
                                    color: headerAction.props?.color || accentColor,
                                    size: headerAction.props?.size || 'large',
                                    variant: headerAction.props?.variant || 'contained',
                                })
                                : headerAction}
                        </Box>
                    ) : null}
                </Stack>

                {chips && chips.length > 0 ? (
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 2.25 }}>
                        {chips.map((chip) => {
                            const active = selectedChip === chip.key;
                            const hasCount = chip.count != null;
                            const count = hasCount ? chip.count : 0;
                            const color = chip.color || (active ? accentColor : 'default');
                            const label = hasCount ? `${chip.label} (${count})` : chip.label;
                            return (
                                <Chip
                                    key={chip.key}
                                    label={label}
                                    onClick={onChipChange ? () => onChipChange(chip.key) : undefined}
                                    color={active ? color : 'default'}
                                    variant={active ? 'filled' : 'outlined'}
                                    sx={{ fontWeight: 600 }}
                                />
                            );
                        })}
                    </Stack>
                ) : null}
            </Box>
        </Box>
    );
}
